import { useEffect } from "react";
import cn from "classnames";
import Styles from "./Toast.module.scss";

export type ToastTone = "loading" | "success" | "error";

interface ToastProps {
  message: string;
  tone?: ToastTone;
  /** Optional action button (e.g. "View" to open the thing that just finished). */
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  /** Auto-dismiss after this many ms. Omit (e.g. while loading) to keep it up until replaced. */
  duration?: number;
}

// Drawn with characters/CSS instead of the Icon component so the toast stays self-contained
// and doesn't depend on which glyphs/fill colors are wired up in the icon map.
const GLYPH: Record<ToastTone, string> = { loading: "", success: "✓", error: "!" };

export const Toast = ({ message, tone = "loading", actionLabel, onAction, onDismiss, duration }: ToastProps) => {
  useEffect(() => {
    if (!duration || !onDismiss) return;
    const handle = setTimeout(onDismiss, duration);
    return () => clearTimeout(handle);
  }, [duration, onDismiss]);

  return (
    <div className={cn(Styles.toast, Styles[tone])} role="status" aria-live="polite">
      <span className={cn(Styles.indicator)} aria-hidden="true">
        {tone === "loading" ? <span className={Styles.spinner} /> : GLYPH[tone]}
      </span>
      <span className={cn(Styles.message)}>{message}</span>
      {actionLabel && onAction ? (
        <button type="button" className={cn(Styles.action)} onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
};
